import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import '../styles/Districts.css';

const Districts = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    superintendent: '',
    email: '',
    phone: '',
    address: '',
    description: ''
  });

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const districtsRef = collection(db, 'districts');
      const snapshot = await getDocs(districtsRef);
      
      const districtsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDistricts(districtsList);
    } catch (error) {
      console.error('Error fetching districts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingDistrict) {
        // Update existing district
        const districtRef = doc(db, 'districts', editingDistrict.id);
        await updateDoc(districtRef, {
          ...formData,
          updatedAt: new Date()
        });
      } else {
        // Add new district
        await addDoc(collection(db, 'districts'), {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      fetchDistricts();
      resetForm();
      toast.success(editingDistrict ? 'District updated.' : 'District added.');
    } catch (error) {
      console.error('Error saving district:', error);
      toast.error('Error saving district. Please try again.');
    }
  };

  const handleEdit = (district) => {
    setEditingDistrict(district);
    setFormData({
      name: district.name || '',
      superintendent: district.superintendent || '',
      email: district.email || '',
      phone: district.phone || '',
      address: district.address || '',
      description: district.description || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (districtId) => {
    const confirmed = await confirm({
      title: 'Delete District',
      message: 'Are you sure you want to delete this district? This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'districts', districtId));
      fetchDistricts();
      toast.success('District deleted.');
    } catch (error) {
      console.error('Error deleting district:', error);
      toast.error('Error deleting district. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      superintendent: '',
      email: '',
      phone: '',
      address: '',
      description: ''
    });
    setEditingDistrict(null);
    setShowAddModal(false);
  };

  if (loading) {
    return (
      <div className="districts-container">
        <div className="loading-spinner">Loading districts...</div>
      </div>
    );
  }

  return (
    <div className="districts-container">
      <div className="districts-header">
        <h1>District Management</h1>
        <button 
          onClick={() => setShowAddModal(true)}
          className="add-district-btn"
        >
          Add New District
        </button>
      </div>

      <div className="districts-grid">
        {districts.length === 0 ? (
          <div className="no-districts">
            <p>No districts found. Add your first district to get started.</p>
          </div>
        ) : (
          districts.map(district => (
            <div key={district.id} className="district-card">
              <div className="district-header">
                <h3>{district.name}</h3>
                <div className="district-actions">
                  <button 
                    onClick={() => handleEdit(district)}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(district.id)}
                    className="delete-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="district-info">
                {district.superintendent && (
                  <p><strong>Superintendent:</strong> {district.superintendent}</p>
                )}
                {district.email && (
                  <p><strong>Email:</strong> {district.email}</p>
                )}
                {district.phone && (
                  <p><strong>Phone:</strong> {district.phone}</p>
                )}
                {district.address && (
                  <p><strong>Address:</strong> {district.address}</p>
                )}
                {district.description && (
                  <p><strong>Description:</strong> {district.description}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingDistrict ? 'Edit District' : 'Add New District'}</h2>
              <button 
                onClick={resetForm}
                className="close-btn"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="district-form">
              <div className="form-group">
                <label htmlFor="name">District Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="superintendent">District Superintendent</label>
                <input
                  type="text"
                  id="superintendent"
                  name="superintendent"
                  value={formData.superintendent}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={resetForm}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="save-btn"
                >
                  {editingDistrict ? 'Update District' : 'Add District'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Districts;
